// Starting text for the four brief sections that open the edit instructions.
//
// A section is stored as one block the user edits directly — they answer the lines they
// care about and leave the rest. Whatever they left unanswered is dropped when the
// instructions are generated, so an editor never receives an empty "Overall Feel:".
//
// ALL-CAPS inside brackets means "this gets replaced". The only such tokens left are the
// [DEFAULT …] ones, which stand for the shared wording edited from the panel's gear: the
// lines the USER fills carry no token at all, because a bracket sitting in a form box is
// something to delete before you can start rather than a hint about what belongs there.
// [Dosaze Logo] stays mixed case because it names an asset rather than asking a question,
// so it reaches the editor as written.
//
// These strings are mirrored in the backend, which renders an untouched plan from the
// same templates: sbly-dash-collector src/creative-intelligence/videoEditing/
// briefSections.js. Keep the two in step — this is what the user starts editing, that
// is what a plan nobody edited renders as.

// A [DEFAULT …] token, matched as a WHOLE token rather than by the substring "[DEFAULT",
// so typing that text into a sentence does not teleport the line into the standing box.
const DEFAULT_TOKEN = /\[DEFAULT [A-Z0-9 ]+\]/;

// What a line the brief is complete without says until someone answers it.
//
// It sits in the TEMPLATE rather than being decoration added to the greyed hint, so
// clicking into a section fills the box with exactly the text that was greyed out a
// moment earlier — a hint that rewrites itself the instant you click it reads as the form
// having lost something.
//
// The cost of that is a line that LOOKS answered: every check for "did the user say
// anything here" has to know this string means they did not — the set/unset dot
// (isWrittenValue below) and the renderer's prune (pruneBriefSection in the backend's
// briefSections.js, which mirrors this constant). Miss one and a brief nobody typed in
// reaches an editor reading "On-Screen Graphics: (optional)".
export const OPTIONAL_HINT = '(optional)';

// The boilerplate sentences are held OUT of these templates, behind [DEFAULT …]
// tokens edited once from the panel's gear. A form full of prose is unreadable and
// invites someone to edit the same standing instruction project by project; the
// template should show the shape of the brief, not restate it every time.
export const HOOK_OVERLAY_TEMPLATE = [
  'Hook:',
  'Timing: [DEFAULT HOOK TIMING]',
  'Visual: [DEFAULT HOOK VISUAL]',
].join('\n');

// Clip/B-Roll Edits is absent on purpose too. It is not something anyone types — the
// timeline already knows the cut, and the renderer appends it under this section when
// the instructions are generated.
// Lines carrying a [DEFAULT …] token sit LAST in every template. They are standing
// wording that is usually fine as-is, so the fields someone actually has to fill come
// first and the defaults settle to the bottom of the box (see BriefSection).
export const EDITING_DIRECTION_TEMPLATE = [
  `Example Video: ${OPTIONAL_HINT}`,
  `Overall Feel: ${OPTIONAL_HINT}`,
  `On-Screen Graphics: ${OPTIONAL_HINT}`,
  'Example Video Notes: [DEFAULT VIDEO EDITING MESSAGE]',
  'General Notes: [DEFAULT EDIT STYLE]',
].join('\n');

// Brand colour and asset are simply what they are — a placeholder asked a question
// that only ever had one answer. The copy leads, since it is the only line here anyone
// actually writes; the other two are standing values that settle to the bottom.
export const FINAL_CTA_TEMPLATE = [
  'Copy:',
  'Brand Color: Blue',
  'Asset: Dosaze Logo',
].join('\n');

// The labels whose line is standing wording, per template.
//
// Derived from the [DEFAULT …] lines rather than listed by hand, and matched by LABEL
// rather than by token, because a standing line stops carrying its token the moment it is
// resolved for editing (see resolveBriefDefaults). Without a label rule, revealing the
// wording would fling the line up into the box of fields to fill and turn the section's
// dot green on a brief nobody had written in.
export function standingPrefixesFor(template: string, extra: string[] = []): string[] {
  const fromTokens = template.split('\n')
    .filter((line) => DEFAULT_TOKEN.test(line) && line.includes(':'))
    .map((line) => `${line.slice(0, line.indexOf(':'))}:`);
  return [...fromTokens, ...extra];
}

export const HOOK_OVERLAY_STANDING_PREFIXES = standingPrefixesFor(HOOK_OVERLAY_TEMPLATE);
export const EDITING_DIRECTION_STANDING_PREFIXES = standingPrefixesFor(EDITING_DIRECTION_TEMPLATE);
// The CTA screen's brand colour and asset are fixed VALUES rather than slots, so the
// token rule alone would leave them sitting among the fields that need answering.
export const FINAL_CTA_STANDING_PREFIXES = standingPrefixesFor(FINAL_CTA_TEMPLATE, ['Brand Color:', 'Asset:']);

// Whether a section asks for nothing in particular — every line it offers is one the brief
// is complete without.
//
// Read off the template rather than declared per section, so adding a line someone must
// answer changes the header's caption on its own instead of leaving it saying "Optional"
// about a field that is not.
export function isOptionalSection(template: string, standingPrefixes: string[] = []): boolean {
  const { inputs } = splitBriefLines(template, standingPrefixes);
  const lines = inputs.split('\n').filter((line) => line.trim());
  return lines.length > 0 && lines.every((line) => {
    const colon = line.indexOf(':');
    return colon !== -1 && line.slice(colon + 1).trim().toLowerCase() === OPTIONAL_HINT;
  });
}

// The standing instructions behind the [DEFAULT …] tokens. Shared across projects and
// edited from the panel's gear, so changing how every brief phrases its timing or its
// editing style is one edit rather than one per project.
export const BRIEF_DEFAULT_KEYS = ['hookTiming', 'hookVisual', 'videoEditingMessage', 'editStyle'] as const;
export type BriefDefaultKey = typeof BRIEF_DEFAULT_KEYS[number];

export const BRIEF_DEFAULT_FIELDS: { key: BriefDefaultKey; label: string; token: string }[] = [
  { key: 'hookTiming', label: 'Hook timing', token: '[DEFAULT HOOK TIMING]' },
  { key: 'hookVisual', label: 'Hook visual', token: '[DEFAULT HOOK VISUAL]' },
  { key: 'videoEditingMessage', label: 'Example video message', token: '[DEFAULT VIDEO EDITING MESSAGE]' },
  { key: 'editStyle', label: 'General notes', token: '[DEFAULT EDIT STYLE]' },
];

export const BRIEF_DEFAULTS: Record<BriefDefaultKey, string> = {
  hookTiming: 'Appear at the very beginning, hold for about 4 seconds, and then fade out. Do not keep on screen for the whole video.',
  hookVisual: 'Black background with white text.',
  videoEditingMessage: 'Please follow its style for pacing, cuts, transitions, zooms, etc.',
  editStyle: 'Please also add cuts/transitions, zoom-ins, etc., where you think it would benefit the video to add viewer interest. Keep the pace quick and remove any dead air. Add any subtle sound effects when appropriate.',
};

// Where the shared defaults live, alongside the shared hook prompt.
export const BRIEF_DEFAULTS_NAMESPACE = 'video-brief-defaults';

// The saved overrides laid over the house wording. A key saved BLANK falls back rather
// than winning: clearing a field in the gear means "I have nothing to add", and the
// renderer already reads it that way (statedOverrides in briefSections.js). Reading it
// differently here would show the panel one brief and print another.
export function mergeBriefDefaults(
  stored?: Partial<Record<BriefDefaultKey, string>> | null,
): Record<BriefDefaultKey, string> {
  const merged = { ...BRIEF_DEFAULTS };
  for (const key of BRIEF_DEFAULT_KEYS) {
    const value = stored ? stored[key] : undefined;
    if (typeof value === 'string' && value.trim()) merged[key] = value.trim();
  }
  return merged;
}

// Swap each [DEFAULT …] token for the sentence it stands for.
//
// Used for DISPLAY — so the panel shows the wording a brief will actually carry instead of
// a bracket — and as the materialiser behind the pencil, which turns the tokens into this
// project's own editable text. `split`/`join` rather than `replace`, because `$&` and
// friends are substitution syntax in a replacement string and every default is someone's
// prose. A default saved across several lines is folded onto one, so a line stays a line
// and cannot re-classify half of itself as a field to fill.
export function resolveBriefDefaults(
  text: string,
  defaults: Record<BriefDefaultKey, string> = BRIEF_DEFAULTS,
): string {
  return BRIEF_DEFAULT_FIELDS.reduce((out, { key, token }) => {
    const wording = (defaults[key] || BRIEF_DEFAULTS[key]).replace(/\s*\n\s*/g, ' ').trim();
    return out.split(token).join(wording);
  }, text || '');
}

// The one line the collapsed standing block shows.
//
// A value only earns its place on that line if it is short enough to read at a glance —
// the shared wording is a paragraph, and "Timing" answers the only question the collapsed
// row is ever asked ("is that covered?") without pushing the panel taller again.
const SHORT_VALUE = 24;

export function standingSummary(block: string): string {
  return (block || '').split('\n').filter((line) => line.trim()).map((line) => {
    const colon = line.indexOf(':');
    const label = (colon === -1 ? line : line.slice(0, colon)).trim();
    const value = colon === -1 ? '' : line.slice(colon + 1).trim();
    const short = !!value && value.length <= SHORT_VALUE && !DEFAULT_TOKEN.test(value);
    return short ? `${label}: ${value}` : label;
  }).join(' · ');
}

// Whether this section's standing wording still SAYS what the shared wording says.
//
// Compared after resolving both, not by looking for a surviving [DEFAULT …] token: opening
// the wording to read it, or opening it and changing nothing, leaves the section holding
// the sentences rather than the tokens — and a row that stops saying "defaults" because
// you looked at it claims a divergence that is not there. What matters is whether the text
// differs, not which spelling of the same text is stored.
export function matchesSharedDefaults(
  block: string,
  templateBlock: string,
  defaults: Record<BriefDefaultKey, string> = BRIEF_DEFAULTS,
): boolean {
  const settle = (text: string) => resolveBriefDefaults(text, defaults).trim();
  return settle(block) === settle(templateBlock);
}

// Subtitles and music only ever say one of two things, so they are picked rather than
// typed. The stored value is the key; the backend turns it into the sentence.
export const BRIEF_CHOICES = [
  { value: 'vibe', label: 'Pick a style that matches the vibe' },
  { value: 'example', label: 'Match the style to the example' },
] as const;

export const DEFAULT_BRIEF_CHOICE = BRIEF_CHOICES[0].value;

// Write the value on a "Label: value" line, matching how briefLineValue READS one, so the
// reader and the writer cannot drift apart. The line's own indent, bullet and casing
// survive; a section with no such line gains one at the end.
//
// Assignment, not String.replace: `$&`, `` $` ``, `$'` and `$$` are substitution syntax in
// a replacement string, so a hook containing one would otherwise come out mangled.
export function setBriefLineValue(text: string, label: string, value: string): string {
  const prefix = `${label.toLowerCase()}:`;
  const lines = (text || '').split('\n');
  const index = lines.findIndex(
    (raw) => raw.trim().replace(/^[*•]\s*/, '').toLowerCase().startsWith(prefix),
  );
  if (index === -1) return text ? `${text}\n${label}: ${value}` : `${label}: ${value}`;
  const colon = lines[index].indexOf(':');
  lines[index] = `${lines[index].slice(0, colon + 1)}${value ? ` ${value}` : ''}`;
  return lines.join('\n');
}

// Put a generated hook into the overlay section without disturbing anything else the
// user wrote there. The hook is one line of that section, so a fresh section is built
// from the template — which is what carries the timing line the hook is supposed to
// arrive with — and only the hook line is written.
export function hookOverlayFor(hookText: string): string {
  return setBriefLineValue(HOOK_OVERLAY_TEMPLATE, 'Hook', hookText.trim());
}

// Whether applying a generated hook to this section would destroy real work or merely
// replace wording we put there ourselves.
//
// `appliedHook` is the hook this project last took from the generator (the plan's own
// `hook` field). It is what separates the two cases that both live on the Hook line:
// swapping one generated hook for another needs no ceremony, while a hook the user TYPED
// is their work and must not vanish without a question — comparing the rest of the
// section alone could not tell those apart, and silently chose to discard.
//
// `defaults` is the shared wording as the panel resolved it. Revealing the standing block
// swaps its tokens for those sentences, which is US writing, not the user — so both
// spellings of an untouched section have to read as untouched.
export function isUntouchedHookOverlay(
  text: string,
  appliedHook?: string | null,
  defaults?: Record<BriefDefaultKey, string>,
): boolean {
  const trimmed = (text || '').trim();
  if (!trimmed) return true;
  const below = (section: string) => section.split('\n').slice(1).join('\n').trim();
  const template = below(HOOK_OVERLAY_TEMPLATE);
  const ours = below(trimmed);
  if (ours !== template && ours !== resolveBriefDefaults(template, defaults).trim()) return false;
  // An empty hook line is boilerplate either way.
  const hookLine = briefLineValue(trimmed, 'Hook');
  if (!isWrittenValue(hookLine)) return true;
  return !!appliedHook && hookLine === appliedHook.trim();
}

// Does this value answer its line, or is it still the shape of an answer? The templates no
// longer seed a [PLACEHOLDER] onto a line anyone fills, but plans already stored do, so a
// surviving one still counts as unwritten however it got there.
const PLACEHOLDER = /\[[^\]]+\]/;

export function isWrittenValue(value: string): boolean {
  const trimmed = value.trim();
  // "(optional)" is what the template seeds an optional line with, so a line still saying
  // it is untouched — the same as an empty one, and the same as a stored [PLACEHOLDER].
  return !!trimmed && !PLACEHOLDER.test(trimmed) && trimmed.toLowerCase() !== OPTIONAL_HINT;
}

function standingLabelOf(line: string, standingPrefixes: string[]): string | null {
  const text = line.trimStart();
  return standingPrefixes.find((prefix) => text.startsWith(prefix)) || null;
}

// Split a section into the lines that need answering and the standing wording that
// settles below them, keeping the classification of every line so an edit can be written
// back where it came from (see recombineBriefLines).
//
// A BLANK line takes the kind of the block it sits in, so a blank line separating two
// default lines stays with the defaults instead of migrating up into the other box.
export interface BriefSectionSplit {
  inputs: string;
  defaults: string;
  kinds: ('input' | 'standing')[];
}

export function splitBriefLines(text: string, standingPrefixes: string[] = []): BriefSectionSplit {
  const lines = text.split('\n');
  const seenLabels = new Set<string>();
  let previous: 'input' | 'standing' = 'input';
  const kinds = lines.map((line): 'input' | 'standing' => {
    if (!line.trim()) return previous;
    if (DEFAULT_TOKEN.test(line)) { previous = 'standing'; return previous; }
    const label = standingLabelOf(line, standingPrefixes);
    // Only the first line with a standing label is the template's own; a second is the
    // user's addition and belongs with their writing rather than dimmed out of the way.
    if (label && !seenLabels.has(label)) {
      seenLabels.add(label);
      previous = 'standing';
      return previous;
    }
    previous = 'input';
    return previous;
  });
  return {
    inputs: lines.filter((_, i) => kinds[i] === 'input').join('\n'),
    defaults: lines.filter((_, i) => kinds[i] === 'standing').join('\n'),
    kinds,
  };
}

// Put the two boxes back into the one block that gets stored, in the ORDER the section
// already had. Emitting inputs-then-defaults instead would silently reorder any section
// whose standing wording does not happen to sit last — which is true of the templates
// today, and so exactly the kind of assumption that breaks quietly later.
//
// Lines added in a box are emitted at the last position of that box's kind, so they land
// beside their own kind rather than at the end of the section.
export function recombineBriefLines(
  split: BriefSectionSplit,
  nextInputs: string,
  nextDefaults: string,
): string {
  const queues = {
    input: nextInputs === '' ? [] : nextInputs.split('\n'),
    standing: nextDefaults === '' ? [] : nextDefaults.split('\n'),
  };
  const lastOf = {
    input: split.kinds.lastIndexOf('input'),
    standing: split.kinds.lastIndexOf('standing'),
  };
  const out: string[] = [];
  split.kinds.forEach((kind, index) => {
    const queue = queues[kind];
    if (index === lastOf[kind]) {
      out.push(...queue);
      queue.length = 0;
    } else if (queue.length) {
      out.push(queue.shift() as string);
    }
  });
  // A kind the section had no line of at all (a section that gains standing wording).
  out.push(...queues.input, ...queues.standing);
  return out.join('\n');
}

// Whether a section says anything of the user's own.
//
// Standing wording is excluded: the CTA screen ships with a real brand colour and asset,
// so "has a line that is not a placeholder" would call that section answered the moment
// it was seeded. This is what the set/unset dot reads, so that the dot and the
// requirement check below cannot contradict each other on the same row.
export function isSectionWritten(text: string | null | undefined, standingPrefixes: string[] = []): boolean {
  const { inputs } = splitBriefLines(text || '', standingPrefixes);
  return inputs.split('\n').some((line) => {
    const bare = line.trim().replace(/^[*•]\s*/, '');
    // A "Label: value" line is answered by its value; a bare line is its own answer.
    return isWrittenValue(bare.includes(':') ? bare.slice(bare.indexOf(':') + 1) : bare);
  });
}

// The value on a "Label: value" line of a brief section, or '' when the line is absent
// or empty. Sections are free text, so this reads them the same way the renderer does
// rather than assuming a shape.
export function briefLineValue(text: string, label: string): string {
  const prefix = `${label.toLowerCase()}:`;
  for (const raw of (text || '').split('\n')) {
    const line = raw.trim().replace(/^[*•]\s*/, '');
    if (line.toLowerCase().startsWith(prefix)) return line.slice(prefix.length).trim();
  }
  return '';
}

// The two things a brief cannot go to an editor without: the words on screen at the
// start, and the words on screen at the end. Everything else in the brief has a sensible
// default or is genuinely optional — these have neither, and an editor receiving a brief
// with no hook has been handed a job they cannot finish.
//
// Read from the SECTION text, because that is what the user types into and what the
// instructions actually print. A bare "Hook:" is the shape of an answer, not an answer.
export function missingBriefRequirements(plan: {
  hookOverlay?: string | null; finalCta?: string | null;
}): { hook: boolean; cta: boolean } {
  const written = (sectionText: string | null | undefined, label: string) => (
    isWrittenValue(briefLineValue(sectionText || '', label))
  );
  // An empty section is covered by `written` returning false for it, so it needs no
  // separate check — the section is required in its own right either way.
  return {
    hook: !written(plan.hookOverlay, 'Hook'),
    cta: !written(plan.finalCta, 'Copy'),
  };
}
